// Dialog "+ Riga manuale": crea una proposta emissione fuori dal detect
// automatico (es. vendita diretta senza ordine Poleepo). Stesso pattern
// modale di MarkDialog/CallLogDialog.
import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { todayLocalISO } from "@/shared/format";
import { createManual } from "../api";
import { DOC_TYPES_BY_KIND, type DocType } from "../types";

const EMISSION_DOC_TYPES = DOC_TYPES_BY_KIND.emission;

export function ManualDialog({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const [customerName, setCustomerName] = useState("");
  const [country, setCountry] = useState("IT");
  const [grossExpected, setGrossExpected] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayLocalISO());
  const [docType, setDocType] = useState<DocType | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC per annullare (allineato a CallLogDialog). Ignorato durante l'invio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async () => {
    if (!customerName.trim()) {
      setError("Il nome cliente è obbligatorio.");
      return;
    }
    if (country.trim().length !== 2) {
      setError("Il paese è un codice ISO2 (es. IT, FR).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createManual({
        customer_name: customerName.trim(),
        country: country.trim().toUpperCase(),
        gross_expected: grossExpected.trim() ? Number(grossExpected) : null,
        invoice_date: invoiceDate,
        doc_type: docType || null,
      });
      onDone();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Riga manuale"
      data-no-open
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-display text-[16px] font-bold text-ink">Riga manuale</h3>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Nome cliente
            <input
              value={customerName}
              disabled={busy}
              autoFocus
              onChange={(e) => setCustomerName(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Paese (ISO2)
            <input
              value={country}
              disabled={busy}
              maxLength={2}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              className="h-[44px] w-20 rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50 font-semibold"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Importo lordo (opzionale)
            <input
              type="number"
              step={0.01}
              min={0}
              inputMode="decimal"
              value={grossExpected}
              disabled={busy}
              onChange={(e) => setGrossExpected(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal tabular-nums text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Data fatturazione
            <input
              type="date"
              value={invoiceDate}
              disabled={busy}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Sigla (opzionale)
            <select
              value={docType}
              disabled={busy}
              onChange={(e) => setDocType(e.target.value as DocType | "")}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            >
              <option value="">auto</option>
              {EMISSION_DOC_TYPES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-md border border-accent bg-accent-2 px-3 py-2 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
